import { ListenableSubscribable } from '@benzinga/subscribable';
import { deepEqual } from '@benzinga/utils';
import { Layout, LayoutList, LayoutListItem } from './entities';
import { extractLayoutFromPersist } from '@benzinga/hooks';
import { PersistStorageKey } from '@benzinga/session';

interface LayoutsListUpdateEvent {
  layoutsList: LayoutList | undefined;
  type: 'layouts:layouts_list_update' | 'layouts:layouts_list_item_update';
}

interface LayoutsListItemUpdateEvent {
  layoutsListItem: LayoutListItem | undefined;
  type: 'layouts:layouts_list_item_update';
}

export type LayoutStoreEvent = LayoutsListUpdateEvent | LayoutsListItemUpdateEvent;

export class LayoutStore extends ListenableSubscribable<LayoutStoreEvent> {
  private date?: Date;
  private layoutsList?: LayoutList;

  constructor() {
    super();
    this.date = undefined;
    this.layoutsList = undefined;
  }

  public static compareLayouts = (lhs: LayoutList, rhs: LayoutList): boolean => {
    return (
      lhs.length === rhs.length &&
      lhs?.every(layout => {
        const newLayout = rhs.find(newLayout => layout.uuid === newLayout.uuid);
        if (newLayout) {
          return deepEqual(layout, newLayout);
        } else {
          return false;
        }
      })
    );
  };

  public shouldWeFetchLayout = (): boolean => {
    const ONE_MIN = 60 * 1000; /* ms */
    const lastCalled = this.date?.getTime() ?? 0; // use this once all Layout are grabbed from manager
    if (this.layoutsList === undefined || Date.now() - lastCalled > ONE_MIN) {
      return true;
    }
    return false;
  };

  public getLayoutsList = (): LayoutList | undefined => {
    return this.layoutsList;
  };

  public getCachedLayout = (): Layout | undefined => {
    const currentLayout = this.layoutsList?.find(
      layout => layout.uuid === window.sessionStorage.getItem(PersistStorageKey.activeLayoutId),
    );
    if (!currentLayout) {
      return undefined;
    }
    const data = extractLayoutFromPersist();
    if (!data) {
      return undefined;
    }

    return {
      data,
      date_created: currentLayout.date_created,
      date_updated: currentLayout.date_updated,
      ip_address: currentLayout.ip_address ?? '',
      is_favorite: currentLayout.is_favorite ?? false,
      name: currentLayout.name ?? '',
      user_agent: currentLayout.user_agent,
      uuid: currentLayout.uuid,
    };
  };

  // returns true if no update was needed;
  public updateLayoutsList = (layoutsList: LayoutList) => {
    if (this.layoutsList && LayoutStore.compareLayouts(this.layoutsList, layoutsList)) {
      this.date = new Date();
    } else {
      this.layoutsList = layoutsList;
      this.dispatch({
        layoutsList: layoutsList,
        type: 'layouts:layouts_list_update',
      });
    }
  };

  public clearLayouts = () => {
    this.layoutsList = [];
    this.dispatch({
      layoutsList: undefined,
      type: 'layouts:layouts_list_update',
    });
  };

  public addLayout = (layout: LayoutListItem): void => {
    if (this.layoutsList) {
      this.layoutsList.push(layout);
    } else {
      this.layoutsList = [layout];
    }

    this.dispatch({
      layoutsList: this.layoutsList,
      type: 'layouts:layouts_list_update',
    });
  };

  public updateLayout = (layout: Layout['data']): void => {
    this.layoutsList = this.layoutsList?.map(item => (item.uuid === layout.uuid ? { ...item, ...layout } : item));

    this.dispatch({
      layoutsListItem: layout,
      type: 'layouts:layouts_list_item_update',
    });
  };

  public removeLayout = (layout: LayoutListItem): void => {
    this.layoutsList = this.layoutsList?.filter(item => item.uuid !== layout.uuid);
  };
}
